const getCartKey = (item) => {
  // If item has a selected calendar date, include it so different dates don't merge
  const dateKey =
    item?.dateSelected?.day && item?.dateSelected?.hour
      ? `${item.dateSelected.day}|${item.dateSelected.hour}`
      : ""

  return `${item._id}__${dateKey}`
}

const clampQty = (nextQty, item) => {
  // Online courses should never be bought more than once per order
  if (item?.type === "online") return Math.min(nextQty, 1)

  // If maxQty exists (availability for that date), clamp to it.
  const max = Number(item?.maxQty)
  if (Number.isFinite(max) && max > 0) return Math.min(nextQty, max)

  return nextQty
}

export const addItemToCart = (cartItems, cartItemToAdd) => {
  const key = getCartKey(cartItemToAdd)
  const existing = cartItems.find((ci) => getCartKey(ci) === key)

  if (existing) {
    return cartItems.map((ci) => {
      if (getCartKey(ci) !== key) return ci
      const currentQty = ci.quantity || 1
      const nextQty = clampQty(currentQty + 1, ci)
      return { ...ci, quantity: nextQty }
    })
  }

  // Ensure maxQty is carried (if provided)
  return [...cartItems, { ...cartItemToAdd, quantity: 1 }]
}

export const removeItemFromCart = (cartItems, cartItemToRemove) => {
  const key = getCartKey(cartItemToRemove)
  const existing = cartItems.find((ci) => getCartKey(ci) === key)
  if (!existing) return cartItems

  const currentQty = existing.quantity || 1
  const nextQty = currentQty - 1

  // If decreasing would remove the item completely:
  if (nextQty <= 0) {
    // If it's a combo, remove combo + its attached items for the same selected date
    if (existing.is_combo) {
      const comboDateKey =
        existing?.dateSelected?.day && existing?.dateSelected?.hour
          ? `${existing.dateSelected.day}|${existing.dateSelected.hour}`
          : ""

      return cartItems.filter((ci) => {
        const sameComboDate =
          comboDateKey &&
          ci?.dateSelected?.day &&
          ci?.dateSelected?.hour &&
          `${ci.dateSelected.day}|${ci.dateSelected.hour}` === comboDateKey

        // remove combo itself
        if (getCartKey(ci) === getCartKey(existing)) return false

        // remove barista + online that belong to the same date
        if (sameComboDate && ci.is_barista) return false
        if (sameComboDate && ci.type === "online") return false

        return true
      })
    }

    // Not combo: remove just that exact line item
    return cartItems.filter((ci) => getCartKey(ci) !== key)
  }

  // Otherwise just decrement quantity
  return cartItems.map((ci) =>
    getCartKey(ci) === key ? { ...ci, quantity: nextQty } : ci,
  )
}

export const clearItemCart = (cartItems, cartItemToClear) => {
  // KEEP your special rule:
  // when removing a combo, also remove the barista and the online that belong to that same date
  // (so remove using the same dateSelected key).
  const isCombo = !!cartItemToClear?.is_combo

  if (isCombo) {
    const comboDateKey =
      cartItemToClear?.dateSelected?.day && cartItemToClear?.dateSelected?.hour
        ? `${cartItemToClear.dateSelected.day}|${cartItemToClear.dateSelected.hour}`
        : ""

    return cartItems.filter((ci) => {
      const sameComboDate =
        comboDateKey &&
        ci?.dateSelected?.day &&
        ci?.dateSelected?.hour &&
        `${ci.dateSelected.day}|${ci.dateSelected.hour}` === comboDateKey

      // remove:
      // - the combo line itself
      // - the barista item of that date
      // - the online course of that date (food safety) if you attached dateSelected to it
      if (getCartKey(ci) === getCartKey(cartItemToClear)) return false
      if (sameComboDate && ci.is_barista) return false
      if (sameComboDate && ci.type === "online") return false

      return true
    })
  }

  // default: remove only that exact line (same date if any)
  const key = getCartKey(cartItemToClear)
  return cartItems.filter((ci) => getCartKey(ci) !== key)
}

// Exported so CheckoutItem can decide if "+" should be disabled
export const getCartItemMaxQty = (item) => {
  const max = Number(item?.maxQty)
  return Number.isFinite(max) && max > 0 ? max : null
}
